/**
 * Cost Estimation Engine for Inspection Findings
 *
 * Automatically calculates repair cost estimates based on:
 * - Finding severity (good, fair, poor, critical)
 * - Category type (brakes, tires, engine, etc.)
 * - Historical data and industry standards
 */

import type { InspectionItemCondition } from '@/types';
import { toIntermediatePrecision } from '@/utils/money';

// Base cost multipliers by severity
const severityMultipliers: Record<InspectionItemCondition, number> = {
  good: 0, // No repair needed
  fair: 0.5, // Minor repair
  poor: 1.0, // Standard repair
  critical: 1.5, // Urgent/complex repair
};

// Base costs in cents for common repair categories
const baseCosts: Record<string, { labor: number; parts: number; description: string }> = {
  // Brakes
  brake_pads: { labor: 8000, parts: 12000, description: 'Jarrupalojen vaihto' },
  brake_discs: { labor: 15000, parts: 35000, description: 'Jarrulevyjen vaihto' },
  brake_fluid: { labor: 5000, parts: 3000, description: 'Jarrunesteen vaihto' },
  brake_calipers: { labor: 20000, parts: 45000, description: 'Jarrusatulan vaihto' },

  // Tires & Suspension
  tire_replacement: { labor: 4000, parts: 18000, description: 'Renkaan vaihto' },
  tire_balance: { labor: 3000, parts: 0, description: 'Renkaiden tasapainotus' },
  wheel_alignment: { labor: 8000, parts: 0, description: 'Pyörien suuntaus' },
  shock_absorbers: { labor: 18000, parts: 35000, description: 'Iskunvaimentimien vaihto' },
  suspension_bushings: { labor: 15000, parts: 12000, description: 'Alustapuslien vaihto' },
  ball_joints: { labor: 20000, parts: 15000, description: 'Pallon ivojen vaihto' },

  // Engine & Cooling
  oil_change: { labor: 4000, parts: 6000, description: 'Öljynvaihto' },
  oil_filter: { labor: 2000, parts: 1500, description: 'Öljynsuodattimen vaihto' },
  air_filter: { labor: 2000, parts: 3000, description: 'Ilmansuodattimen vaihto' },
  coolant_flush: { labor: 6000, parts: 4000, description: 'Jäähdytysnesteen vaihto' },
  radiator_replacement: { labor: 25000, parts: 45000, description: 'Jäähdyttimen vaihto' },
  thermostat: { labor: 12000, parts: 8000, description: 'Termostaatin vaihto' },
  timing_belt: { labor: 45000, parts: 25000, description: 'Jakohihnan vaihto' },
  spark_plugs: { labor: 8000, parts: 12000, description: 'Sytytystulppien vaihto' },

  // Electrical
  battery_replacement: { labor: 3000, parts: 15000, description: 'Akun vaihto' },
  alternator: { labor: 25000, parts: 45000, description: 'Laturin vaihto' },
  starter_motor: { labor: 30000, parts: 40000, description: 'Käynnistinmoottorin vaihto' },
  headlight_bulb: { labor: 2000, parts: 3500, description: 'Ajovalon lampun vaihto' },
  headlight_assembly: { labor: 8000, parts: 25000, description: 'Ajovalon vaihto' },

  // Exhaust
  exhaust_repair: { labor: 15000, parts: 20000, description: 'Pakoputken korjaus' },
  catalytic_converter: { labor: 20000, parts: 80000, description: 'Katalysaattorin vaihto' },
  muffler: { labor: 12000, parts: 18000, description: 'Äänenvaimentin vaihto' },

  // Safety & Interior
  windshield_wipers: { labor: 1500, parts: 4000, description: 'Tuulilasinpyyhkimien vaihto' },
  windshield_replacement: { labor: 15000, parts: 45000, description: 'Tuulilasin vaihto' },
  cabin_air_filter: { labor: 2500, parts: 3000, description: 'Sisäilmansuodattimen vaihto' },
  door_lock_mechanism: { labor: 18000, parts: 15000, description: 'Oven lukitusmekanismin korjaus' },

  // Default for unknown items
  default: { labor: 10000, parts: 15000, description: 'Korjaus' },
};

// Pattern matching for item names to repair types
const itemPatterns: Record<string, string> = {
  // Finnish patterns
  jarrupala: 'brake_pads',
  jarrulevy: 'brake_discs',
  jarruneste: 'brake_fluid',
  jarrusatula: 'brake_calipers',

  rengas: 'tire_replacement',
  renkaa: 'tire_replacement',
  tasapaino: 'tire_balance',
  suuntaus: 'wheel_alignment',
  iskari: 'shock_absorbers',
  iskunvaimennin: 'shock_absorbers',
  pusli: 'suspension_bushings',
  pallonivel: 'ball_joints',

  öljy: 'oil_change',
  'öljynsuodatin': 'oil_filter',
  ilmansuodatin: 'air_filter',
  jäähdytin: 'radiator_replacement',
  jäähdytysneste: 'coolant_flush',
  termostaatti: 'thermostat',
  jakohihna: 'timing_belt',
  sytytystulppa: 'spark_plugs',

  akku: 'battery_replacement',
  laturi: 'alternator',
  käynnistin: 'starter_motor',
  ajovalo: 'headlight_assembly',
  lamppu: 'headlight_bulb',

  pakopputki: 'exhaust_repair',
  katalysaattori: 'catalytic_converter',
  äänenvaimennin: 'muffler',

  pyyhkijä: 'windshield_wipers',
  tuulilasi: 'windshield_replacement',
  sisäilmansuodatin: 'cabin_air_filter',

  // English patterns (for testing)
  brake: 'brake_pads',
  pad: 'brake_pads',
  disc: 'brake_discs',
  rotor: 'brake_discs',
  tire: 'tire_replacement',
  shock: 'shock_absorbers',
  oil: 'oil_change',
  filter: 'oil_filter',
  battery: 'battery_replacement',
};

export interface CostEstimate {
  laborCents: number;
  partsCents: number;
  totalCents: number;
  description: string;
  confidence: 'high' | 'medium' | 'low'; // Estimation confidence
  breakdown?: {
    baseLabor: number;
    baseParts: number;
    severityMultiplier: number;
  };
}

/**
 * Estimate repair cost based on item name and condition
 */
export function estimateRepairCost(
  itemName: string,
  condition: InspectionItemCondition,
  category?: string
): CostEstimate | null {
  // No cost for items in good condition
  if (condition === 'good') {
    return null;
  }

  // Find matching repair type from item name
  const lowerName = itemName.toLowerCase();
  let repairType = 'default';
  let confidence: 'high' | 'medium' | 'low' = 'low';

  // Try to match pattern
  for (const [pattern, type] of Object.entries(itemPatterns)) {
    if (lowerName.includes(pattern.toLowerCase())) {
      repairType = type;
      confidence = 'high';
      break;
    }
  }

  // If no specific pattern matched, try category-based estimation
  if (repairType === 'default' && category) {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('brake')) {
      repairType = 'brake_pads';
      confidence = 'medium';
    } else if (categoryLower.includes('tire') || categoryLower.includes('rengas')) {
      repairType = 'tire_replacement';
      confidence = 'medium';
    } else if (categoryLower.includes('electrical') || categoryLower.includes('sähkö')) {
      repairType = 'battery_replacement';
      confidence = 'medium';
    } else if (categoryLower.includes('engine') || categoryLower.includes('moottori')) {
      repairType = 'oil_change';
      confidence = 'medium';
    }
  }

  const baseCost = baseCosts[repairType] ?? baseCosts['default']!;
  const multiplier = severityMultipliers[condition];

  // Calculate costs with severity multiplier. Snap to 4-decimal intermediate
  // precision before the final whole-cent round to avoid floating-point drift.
  const laborCents = Math.round(toIntermediatePrecision(baseCost.labor * (1 + multiplier * 0.5))); // Labor increases 50% of multiplier
  const partsCents = Math.round(toIntermediatePrecision(baseCost.parts * (1 + multiplier))); // Parts increase fully with multiplier

  return {
    laborCents,
    partsCents,
    totalCents: laborCents + partsCents,
    description: baseCost.description,
    confidence,
    breakdown: {
      baseLabor: baseCost.labor,
      baseParts: baseCost.parts,
      severityMultiplier: multiplier,
    },
  };
}

/**
 * Estimate total cost for multiple findings
 */
export function estimateTotalCost(
  findings: Array<{ itemName: string; condition: InspectionItemCondition; category?: string }>
): {
  totalLaborCents: number;
  totalPartsCents: number;
  totalCents: number;
  itemCount: number;
  estimates: CostEstimate[];
} {
  const estimates = findings
    .map(f => estimateRepairCost(f.itemName, f.condition, f.category))
    .filter((e): e is CostEstimate => e !== null);

  const totalLaborCents = estimates.reduce((sum, e) => sum + e.laborCents, 0);
  const totalPartsCents = estimates.reduce((sum, e) => sum + e.partsCents, 0);

  return {
    totalLaborCents,
    totalPartsCents,
    totalCents: totalLaborCents + totalPartsCents,
    itemCount: estimates.length,
    estimates,
  };
}

/**
 * Get urgency level based on condition
 */
export function getUrgencyLevel(condition: InspectionItemCondition): 'immediate' | 'soon' | 'future' {
  switch (condition) {
    case 'critical':
      return 'immediate';
    case 'poor':
      return 'soon';
    case 'fair':
      return 'future';
    default:
      return 'future';
  }
}

/**
 * Format price in euros
 */
export function formatPrice(cents: number): string {
  return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
}
